<script setup>
// Photo Collage Maker（#19 P3）。复刻 design 的 Photo Collage.html 内联脚本：
// 多图上传 → 选网格布局 + 间距 + 背景色 → canvas 实时 cover-fit 拼接 → 下载 PNG。
// 纯前端：FileReader/Image + Canvas，图片不离开浏览器。复用 .ft-scope 全局类(dropzone/workspace/ws-*)。
import { ref, computed, nextTick, onBeforeUnmount } from "vue";
import { fileTooLargeMsg, looksLikeEditableImage } from "@/settings/free-tools/batch-limits.js";
import { useCanvasDecode } from "@/composables/useCanvasDecode";

const props = defineProps({
  mode: { type: String, default: "grid" }, // 单工具，mode 可选
  slug: { type: String, default: "" },
});

const { downloadBlob } = useFreeToolsDownload();
const { decodeForCanvas, decodeErrorMsg } = useCanvasDecode();

const LAYOUTS = [
  { id: "2h", title: "Side by side", cols: 2, rows: 1, gridStyle: { gridTemplateColumns: "1fr 1fr" }, cells: 2 },
  { id: "2v", title: "Stacked", cols: 1, rows: 2, gridStyle: { gridTemplateRows: "1fr 1fr" }, cells: 2 },
  { id: "3h", title: "Three across", cols: 3, rows: 1, gridStyle: { gridTemplateColumns: "1fr 1fr 1fr" }, cells: 3 },
  { id: "4", title: "2×2 grid", cols: 2, rows: 2, gridStyle: { gridTemplateColumns: "1fr 1fr", gridTemplateRows: "1fr 1fr" }, cells: 4 },
];
const BGS = ["#ffffff", "#000000", "#8b5cf6", "#ff6f9c", "#fbbf24"];

const stage = ref("drop"); // drop | work
const layout = ref("2h");
const gap = ref(10);
const bg = ref("#ffffff");
const dragging = ref(false);
const fileInput = ref(null);
const canvas = ref(null);

let imgs = []; // 已加载的 HTMLImageElement
const objectUrls = []; // 待回收的 object URL
const count = ref(0);

const countInfo = computed(() => count.value + (count.value === 1 ? " photo" : " photos"));

function revokeAll() {
  while (objectUrls.length) {
    try { URL.revokeObjectURL(objectUrls.pop()); } catch (_) {}
  }
}

async function addFiles(files) {
  const list = [...files].filter((f) => looksLikeEditableImage(f));
  for (const f of list) {
    const _m = fileTooLargeMsg(f, "image");
    if (_m) { alert(_m); return; }
  }
  let pending = list.length;
  if (!pending) return;
  const done = () => {
    if (--pending === 0) {
      if (imgs.length) { stage.value = "work"; nextTick(render); }
    }
  };
  for (const f of list) {
    // 浏览器解不了的源(heic/tiff/dds/psd…)先经后端转 png,再进 canvas(用户无感)。
    let _f;
    try { _f = await decodeForCanvas(f); }
    catch (e) { alert(decodeErrorMsg(f)); done(); continue; }
    const im = new Image();
    const url = URL.createObjectURL(_f);
    objectUrls.push(url);
    im.onload = () => {
      imgs.push(im);
      count.value = imgs.length;
      done();
    };
    im.onerror = () => { done(); };
    im.src = url;
  }
}

function render() {
  const c = canvas.value;
  if (!c || !imgs.length) return;
  const ctx = c.getContext("2d");
  const lo = LAYOUTS.find((l) => l.id === layout.value) || LAYOUTS[0];
  const g = +gap.value;
  const S = 900;
  const cols = lo.cols, rows = lo.rows;
  const cellW = (S - g * (cols + 1)) / cols;
  const cellH = cellW;
  const W = S, H = cellH * rows + g * (rows + 1);
  c.width = W; c.height = H;
  ctx.fillStyle = bg.value;
  ctx.fillRect(0, 0, W, H);
  const n = cols * rows;
  for (let i = 0; i < n; i++) {
    const im = imgs[i];
    if (!im) continue;
    const cx = g + (i % cols) * (cellW + g);
    const cy = g + Math.floor(i / cols) * (cellH + g);
    // cover-fit：按单元格宽高比裁中心
    const ar = im.naturalWidth / im.naturalHeight, car = cellW / cellH;
    let sw, sh, sx, sy;
    if (ar > car) { sh = im.naturalHeight; sw = sh * car; sx = (im.naturalWidth - sw) / 2; sy = 0; }
    else { sw = im.naturalWidth; sh = sw / car; sx = 0; sy = (im.naturalHeight - sh) / 2; }
    ctx.drawImage(im, sx, sy, sw, sh, cx, cy, cellW, cellH);
  }
}

function pick() { fileInput.value && fileInput.value.click(); }
function onInput(e) {
  const files = e.target.files;
  if (files && files.length) addFiles(files);
  e.target.value = "";
}
function onDrop(e) {
  e.preventDefault();
  dragging.value = false;
  if (e.dataTransfer && e.dataTransfer.files) addFiles(e.dataTransfer.files);
}

function setLayout(id) { layout.value = id; render(); }
function setBg(v) { bg.value = v; render(); }
function onGap() { render(); }

let lastDlUrl = "";
function download() {
  const c = canvas.value;
  if (!c) return;
  c.toBlob((b) => downloadBlob(b, "collage.png"), "image/png");
}

function reset() {
  stage.value = "drop";
  imgs = [];
  count.value = 0;
  revokeAll();
  if (fileInput.value) fileInput.value.value = "";
}

onBeforeUnmount(() => {
  revokeAll();
  if (lastDlUrl) { try { URL.revokeObjectURL(lastDlUrl); } catch (_) {} }
});
</script>

<template>
  <div class="coll">
    <input ref="fileInput" type="file" accept="image/*" multiple hidden @change="onInput" />

    <!-- 拖放区 -->
    <div v-show="stage === 'drop'" class="dropzone" :class="{ drag: dragging }"
      @click="pick" @dragenter.prevent="dragging = true" @dragover.prevent="dragging = true"
      @dragleave.prevent="dragging = false" @drop="onDrop">
      <div class="dz-ico"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="8" height="8" rx="1" /><rect x="13" y="3" width="8" height="8" rx="1" /><rect x="3" y="13" width="8" height="8" rx="1" /><rect x="13" y="13" width="8" height="8" rx="1" /></svg></div>
      <h3>Drop 2–4 photos here</h3>
      <div class="hint">or click to browse — PNG, JPG, WEBP</div>
      <div class="formats"><span>PNG</span><span>JPG</span><span>WEBP</span><span>2–4 photos</span></div>
      <div class="dz-or">Made right in your browser — never stored</div>
      <button class="btn btn-amber dz-btn" type="button" @click.stop="pick">Choose Photos</button>
    </div>

    <!-- 工作区 -->
    <div v-if="stage === 'work'" class="workspace show">
      <div class="ws-grid">
        <div class="ws-preview"><canvas ref="canvas"></canvas></div>
        <div class="ws-panel">
          <div class="ws-file">
            <div class="thumb" style="display:grid;place-items:center;color:var(--c-free)"><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><rect x="3" y="3" width="8" height="8" rx="1" /><rect x="13" y="3" width="8" height="8" rx="1" /><rect x="3" y="13" width="8" height="8" rx="1" /><rect x="13" y="13" width="8" height="8" rx="1" /></svg></div>
            <div class="meta"><div class="fn">collage</div><div class="fsz">{{ countInfo }}</div></div>
            <span class="rm" title="Remove" @click="reset"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M6 6l12 12M18 6L6 18" stroke-linecap="round" /></svg></span>
          </div>

          <div class="ctl">
            <label>Layout</label>
            <div class="layout-grid">
              <button v-for="l in LAYOUTS" :key="l.id" type="button" :title="l.title"
                :class="{ active: layout === l.id }" :style="l.gridStyle" @click="setLayout(l.id)">
                <span v-for="i in l.cells" :key="i"></span>
              </button>
            </div>
          </div>

          <div class="ctl">
            <label>Gap <span style="color:#f4cd76">{{ gap }}px</span></label>
            <div class="rangewrap"><input type="range" min="0" max="40" v-model="gap" @input="onGap" /></div>
          </div>

          <div class="ctl">
            <label>Background</label>
            <div class="sw-row">
              <span v-for="c in BGS" :key="c" class="sw" :class="{ active: bg === c }" :style="{ background: c }" @click="setBg(c)"></span>
            </div>
          </div>

          <div class="ws-actions">
            <button class="btn btn-amber btn-lg" type="button" @click="download">Download collage</button>
            <button class="btn btn-outline" type="button" @click="pick">Add more photos</button>
            <button class="btn btn-ghost" type="button" @click="reset">Start over</button>
          </div>
        </div>
      </div>
    </div>

    <div class="trust">
      <span class="i"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 2l8 4v6c0 5-3.5 8-8 10-4.5-2-8-5-8-10V6z" /></svg>Private — files stay on your device</span>
      <span class="i"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M13 2L3 14h7l-1 8 10-12h-7z" /></svg>Live preview</span>
      <span class="i"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="9" /><path d="M12 7v10M7 12h10" /></svg>Unlimited &amp; free</span>
    </div>
  </div>
</template>

<style scoped>
/* 仅 layout-grid 是 design 内联 <style> 独有，其余复用 .ft-scope 全局类 */
.coll { width: 100%; }
.layout-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; }
.layout-grid button { aspect-ratio: 1; border: 1px solid var(--line); border-radius: 9px; background: var(--surface-2); cursor: pointer; display: grid; gap: 2px; padding: 6px; transition: 0.15s; }
.layout-grid button.active { border-color: var(--c-free); background: color-mix(in oklab, var(--c-free) 14%, transparent); }
.layout-grid button span { background: var(--tx-faint); border-radius: 2px; }
.trust { display: flex; gap: 22px; justify-content: center; flex-wrap: wrap; margin-top: 20px; }
.trust .i { display: inline-flex; align-items: center; gap: 7px; font-size: 13px; font-weight: 600; color: var(--tx-dim); }
.trust .i svg { width: 16px; height: 16px; color: var(--c-free); }
</style>
